import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { loginUser } from '../firebase/firebaseAuth';
import '../assets/css/registration-modal.css';
import ReCaptcha from './ReCaptcha';

interface LoginModalProps {
  show: boolean;
  onHide: () => void;
  onLogin: (userData: any) => void;
  infoMessage?: string | null;
}

const LoginModal: React.FC<LoginModalProps> = ({ show, onHide, onLogin, infoMessage }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const { language } = useLanguage();

  const t = {
    fr: {
      title: 'Connexion',
      emailLabel: 'Adresse e-mail',
      emailPlaceholder: 'Entrez votre adresse e-mail',
      passwordLabel: 'Mot de passe',
      passwordPlaceholder: 'Entrez votre mot de passe',
      rememberMe: 'Se rappeler de moi',
      forgotPassword: 'Mot de passe oublié?',
      loginBtn: 'Se connecter',
      cancelBtn: 'Annuler',
      errorMessage: 'Email ou mot de passe incorrect',
      loadingText: 'Connexion en cours...'
    },
    en: {
      title: 'Login',
      emailLabel: 'Email address',
      emailPlaceholder: 'Enter your email address',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      loginBtn: 'Login',
      cancelBtn: 'Cancel',
      errorMessage: 'Incorrect email or password',
      loadingText: 'Logging in...'
    }
  } as const;

  const tr = t[language];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔍 Tentative de connexion avec vérification Firestore...');
      const result = await loginUser(email, password);
      
      if (result.success && 'user' in result) {
        console.log('✅ Connexion réussie, données utilisateur:', result.user);
        onLogin(result.user);
        setEmail('');
        setPassword('');
        setRememberMe(false);
        onHide();
      } else {
        console.log('❌ Erreur de connexion:', result.error);
        
        // Vérifier si c'est un compte inactif
        if (result.error && result.error.includes('attente d\'activation')) {
          setError('⚠️ ' + result.error);
        } else {
          setError(result.error || tr.errorMessage);
        }
      }
    } catch (err: any) {
      console.error('❌ Erreur de connexion:', err);
      setError(tr.errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setRememberMe(false);
    setShowPassword(false);
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="login-modal">
      <Modal.Header closeButton className="custom-modal-header">
        <Modal.Title className="custom-modal-title">
          <i className="bi bi-person-circle me-2"></i>
          {tr.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">
        <div className="mt-2 mb-2">
          {infoMessage && (
            <Alert variant="success" className="mb-3">
              <i className="bi bi-check-circle me-2"></i>
              {infoMessage}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
          {error && (
            <Alert 
              variant={error.includes('attente d\'activation') ? 'warning' : 'danger'} 
              className="mb-3"
            >
              <i className={`bi ${error.includes('attente d\'activation') ? 'bi-hourglass-split' : 'bi-exclamation-triangle'} me-2`}></i>
              {error}
            </Alert>
          )}
          
          <Form.Group className="mb-4">
            <Form.Label className="subtitle-aptos">{tr.emailLabel}</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="email"
                placeholder={tr.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="ps-5"
              />
              <i className="bi bi-envelope position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="subtitle-aptos">{tr.passwordLabel}</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder={tr.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="ps-5 pe-5"
              />
              <i className="bi bi-lock position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
              <i 
                className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} position-absolute top-50 end-0 translate-middle-y me-3 text-muted`}
                style={{ cursor: 'pointer' }}
                onClick={() => setShowPassword(!showPassword)}
              ></i>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <Form.Check
                type="checkbox"
                id="rememberMe"
                label={tr.rememberMe}
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="subtitle-aptos mb-0"
              />
              <a 
                href="#" 
                className="text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  // TODO: Implémenter la fonctionnalité de réinitialisation du mot de passe
                }}
                style={{ color: '#424272', fontFamily: "'Montserrat', sans-serif", fontWeight: 400 }}
              >
                {tr.forgotPassword}
              </a>
            </div>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button 
              type="submit" 
              variant="primary" 
              className="flex-fill choice-button"
              disabled={loading || !recaptchaToken}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {tr.loadingText}
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {tr.loginBtn}
                </>
              )}
            </Button>
            
            <Button 
              variant="outline-secondary" 
              className="flex-fill choice-button"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="bi bi-x-circle me-2"></i>
              {tr.cancelBtn}
            </Button>
          </div>
          <div className="mt-3">
            {!recaptchaToken && (
              <small className="text-muted">Veuillez valider le reCAPTCHA avant de vous connecter.</small>
            )}
            <ReCaptcha onChange={(token) => setRecaptchaToken(token)} />
          </div>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
